import { http } from "./client";
import type { PageParams } from "@/types/api";
import type {
  EmployeeOnboarding,
  OnboardingChecklistItem,
  OnboardingChecklistTemplateItem,
  OnboardingStatus,
  OnboardingTaskPhase,
  OnboardingTaskPriority,
  OnboardingTaskRole,
  Policy,
  PolicyAcknowledgement,
  ProbationEvaluation,
  ProbationRecommendation,
  ProbationRecord,
  ProbationReview,
  ProbationDisplayStatus,
  ProbationExtension,
  ProbationStats,
  SeparationPolicy,
} from "@/types/domain";

export interface StartProbationInput {
  start_date: string;
  duration_months?: number;
}

export interface OnboardingFilters extends PageParams {
  employee_id?: string;
  status?: OnboardingStatus | "";
}

export interface ProbationFilters extends PageParams {
  employee_id?: string;
  /** Filters on the displayed status, review_overdue included. */
  status?: ProbationDisplayStatus | "";
}

/** An extension: months added to the current end date, or a new end date. */
export type ProbationExtensionInput = { extension_months: number } | { new_expected_date: string };


export const employeeLifecycleApi = {
  // Onboarding
  startOnboarding: (employeeId: string, input: { start_date: string; hr_owner_id?: string }) =>
    http.post<EmployeeOnboarding>(`/employees/${employeeId}/onboarding`, input),
  getOnboarding: (employeeId: string) => http.get<EmployeeOnboarding>(`/employees/${employeeId}/onboarding`),
  myOnboarding: () => http.get<EmployeeOnboarding>("/employee/self/onboarding"),
  updateOnboarding: (employeeId: string, checklist: OnboardingChecklistItem[], notes: string) =>
    http.put<EmployeeOnboarding>(`/employees/${employeeId}/onboarding`, { checklist, notes }),
  completeOnboarding: (employeeId: string) =>
    http.post<EmployeeOnboarding>(`/employees/${employeeId}/onboarding/complete`, {}),
  completeTask: (employeeId: string, code: string) =>
    http.post<EmployeeOnboarding>(`/employees/${employeeId}/onboarding/tasks/${code}/complete`, {}),
  assignTask: (employeeId: string, code: string, assignedUserId: string) =>
    http.post<EmployeeOnboarding>(`/employees/${employeeId}/onboarding/tasks/${code}/assign`, { assigned_user_id: assignedUserId }),
  listOnboardings: (filters: OnboardingFilters = {}) => http.getPage<EmployeeOnboarding>("/onboardings", filters),

  // Onboarding checklist templates
  listChecklistTemplates: (includeInactive = false) =>
    http.get<OnboardingChecklistTemplateItem[]>("/settings/onboarding-checklist-templates", { include_inactive: includeInactive }),
  createChecklistTemplate: (input: {
    code: string;
    title: string;
    description?: string;
    role: OnboardingTaskRole;
    phase: OnboardingTaskPhase;
    priority?: OnboardingTaskPriority;
    depends_on?: string[];
    due_offset_days?: number;
    sort_order?: number;
  }) => http.post<OnboardingChecklistTemplateItem>("/settings/onboarding-checklist-templates", input),
  updateChecklistTemplate: (id: string, input: Partial<Omit<OnboardingChecklistTemplateItem, "id" | "code">>) =>
    http.put<OnboardingChecklistTemplateItem>(`/settings/onboarding-checklist-templates/${id}`, input),
  deleteChecklistTemplate: (id: string) => http.delete<void>(`/settings/onboarding-checklist-templates/${id}`),

  // Probation
  startProbation: (employeeId: string, input: StartProbationInput) =>
    http.post<ProbationRecord>(`/employees/${employeeId}/probation`, input),
  getProbation: (employeeId: string) => http.get<ProbationRecord>(`/employees/${employeeId}/probation`),
  listProbations: (filters: ProbationFilters = {}) => http.getPage<ProbationRecord>("/probations", filters),
  confirmProbation: (id: string, effectiveDate: string, note?: string, evaluation?: ProbationEvaluation) =>
    http.post<ProbationRecord>(`/probations/${id}/confirm`, { effective_date: effectiveDate, note, evaluation }),
  probationStats: () => http.get<ProbationStats>("/probations/stats"),
  probationExtensions: (id: string) => http.get<ProbationExtension[]>(`/probations/${id}/extensions`),
  extendProbation: (id: string, extension: ProbationExtensionInput, reason: string, evaluation?: ProbationEvaluation) =>
    http.post<ProbationRecord>(`/probations/${id}/extend`, { ...extension, reason, evaluation }),
  failProbation: (id: string, reason: string, evaluation?: ProbationEvaluation) =>
    http.post<ProbationRecord>(`/probations/${id}/fail`, { reason, evaluation }),
  submitManagerReview: (id: string, recommendation: ProbationRecommendation, evaluation: ProbationEvaluation) =>
    http.post<ProbationReview>(`/probations/${id}/reviews`, { recommendation, ...evaluation }),
  listProbationReviews: (id: string) => http.get<ProbationReview[]>(`/probations/${id}/reviews`),

  // Separation policy
  getSeparationPolicy: () => http.get<SeparationPolicy>("/settings/separation-policy"),
  updateSeparationPolicy: (input: Partial<Omit<SeparationPolicy, "id">>) =>
    http.put<SeparationPolicy>("/settings/separation-policy", input),
};

export const policiesApi = {
  list: (includeInactive = false) => http.get<Policy[]>("/policies", { include_inactive: includeInactive }),
  get: (id: string) => http.get<Policy>(`/policies/${id}`),
  create: (input: { title: string; category?: string; content: string; requires_ack?: boolean }) =>
    http.post<Policy>("/policies", input),
  update: (id: string, input: Partial<{ title: string; category: string; content: string; requires_ack: boolean; is_active: boolean }>) =>
    http.put<Policy>(`/policies/${id}`, input),
  acknowledge: (id: string) => http.post<PolicyAcknowledgement>(`/policies/${id}/acknowledge`, {}),
  myPending: () => http.get<Policy[]>("/employee/self/policies/pending"),
  myAcknowledgements: () => http.get<PolicyAcknowledgement[]>("/employee/self/policies/acknowledgements"),
};
